import { useEffect, useState, useCallback } from 'react';
import { Mail, RefreshCw, PlayCircle, RotateCcw, Ban, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from './ui/card';

const Stat = ({ label, value, tone, testId }) => (
  <div className={`rounded-xl border p-3 text-center ${tone}`} data-testid={testId}>
    <div className="text-2xl font-bold">{value ?? '—'}</div>
    <div className="text-[11px] mt-0.5 opacity-80">{label}</div>
  </div>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('ar', { timeZone: 'Asia/Muscat', dateStyle: 'short', timeStyle: 'short' }) : '');

export const EmailQueueCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { const res = await api.get('/admin/email-queue'); setData(res.data); } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (key, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    try { const res = await api.post(path); toast.success(res.data.message); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'فشلت العملية'); }
    finally { setBusy(''); }
  };

  const btn = 'flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50';
  const c = data?.counts || {};

  return (
    <Card className="p-6 max-w-2xl mx-auto space-y-4" data-testid="email-queue-card">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-accent flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> طابور الإيميلات</h2>
        <button onClick={load} className="p-2 rounded-lg hover:bg-muted" data-testid="email-queue-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {data?.paused && (
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="email-queue-paused">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{data.pause_reason || 'الإرسال متوقف مؤقتاً'} — سيُعاد المحاولة تلقائياً بعد {fmt(data.paused_until)}</span>
        </div>
      )}

      {data?.legacy_script && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800" data-testid="email-queue-legacy-hint">
          سكربت Google المنشور حالياً هو النسخة القديمة، لذلك لا يظهر الرصيد المتبقي. انشر النسخة الجديدة من Code.gs لتفعيل عرض الرصيد والإرسال الدقيق.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="الرصيد المتبقي اليوم" value={data?.remaining_quota} tone="bg-blue-50 border-blue-100 text-blue-800" testId="email-queue-remaining" />
        <Stat label={`منتظرة (${data?.pending_individual ?? 0} فردية)`} value={c.pending} tone="bg-amber-50 border-amber-100 text-amber-800" testId="email-queue-pending" />
        <Stat label="أُرسلت آخر 24 ساعة" value={data?.sent_24h} tone="bg-green-50 border-green-100 text-green-800" testId="email-queue-sent" />
        <Stat label="فشلت" value={c.failed} tone="bg-red-50 border-red-100 text-red-800" testId="email-queue-failed" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button className={`${btn} border-primary/30 text-primary hover:bg-primary/5`} disabled={!!busy} onClick={() => act('resume', '/admin/email-queue/resume')} data-testid="email-queue-resume-btn">
          {busy === 'resume' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} استئناف الآن
        </button>
        <button className={`${btn} border-gray-200 text-gray-700 hover:bg-gray-50`} disabled={!!busy || !c.failed} onClick={() => act('retry', '/admin/email-queue/retry-failed')} data-testid="email-queue-retry-btn">
          {busy === 'retry' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} إعادة الفاشلة
        </button>
        <button className={`${btn} border-red-200 text-red-700 hover:bg-red-50`} disabled={!!busy || !data?.pending_bulk} onClick={() => act('cancel', '/admin/email-queue/cancel-bulk', 'إلغاء كل الإيميلات الجماعية المنتظرة؟')} data-testid="email-queue-cancel-bulk-btn">
          {busy === 'cancel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} إلغاء الجماعية المنتظرة ({data?.pending_bulk ?? 0})
        </button>
      </div>

      {data?.recent_failed?.length > 0 && (
        <div className="space-y-1 text-xs" data-testid="email-queue-failed-list">
          {data.recent_failed.map((f) => (
            <div key={f.id} className="flex justify-between gap-3 border-b border-dashed py-1">
              <span className="font-mono truncate" dir="ltr">{f.email}</span>
              <span className="text-red-600 truncate">{f.error}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        حساب Gmail المجاني يسمح بحوالي 100 رسالة يومياً. الرسائل الفردية (الإنجازات والتقارير) تُرسل أولاً، والزائد يُؤجَّل ويُرسل تلقائياً عند تجدد الرصيد دون أن يضيع.
      </p>
    </Card>
  );
};
